import { SHARE_TOKEN_PATTERN, type AccessWish } from "@fusiqo/vozqi-shared";

/**
 * THE POSTER'S TOKEN, carried across the sign-up round trip (spec 2026-10-19
 * §7.2): a stranger scans the workshop's poster, `/c/<token>` asks her to
 * sign in or sign up first, and the token is what brings her back to the
 * poster's page afterwards instead of to an empty app.
 *
 * `sessionStorage`: a one-shot intent of this visit, and a stale token must
 * never reopen the poster on some unrelated sign-in later. (`pending-invite.ts`
 * uses `localStorage` with a 24-hour expiry instead, so a sign-in finished in
 * another tab keeps its code.) Written by the poster's page when it is opened signed
 * out, read by `Gate` once she is signed in, and spent by the page when it
 * opens signed in -- so a later sign-in in the same tab does not reopen it.
 */
const STORAGE_KEY = "pendingSignupToken";

export function readPendingSignup(): string | null {
  try {
    const token = sessionStorage.getItem(STORAGE_KEY);
    return token !== null && SHARE_TOKEN_PATTERN.test(token) ? token : null;
  } catch {
    // Private browsing, or a full quota: behave as if there were none.
    return null;
  }
}

/** Only what has a token's shape: it ends up in an address. */
export function writePendingSignup(token: string): void {
  if (!SHARE_TOKEN_PATTERN.test(token)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // The page still works for this visit; it only loses the round trip.
  }
}

export function clearPendingSignup(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to undo if storage cannot be touched.
  }
}

/**
 * WHAT SHE WROTE ON THE PROFILE'S REQUEST (spec 2026-10-20 §6.1): «Pedir
 * servicio» asks first and for the account after, so her words, her day, her
 * name and her WhatsApp wait here -- beside the token, for the same reason --
 * while she creates the account and types the code. Read back only by the
 * request of the same business; spent once it is sent.
 */
export type SignupDraft = { token: string; name: string; phone: string; wish: AccessWish };

const DRAFT_KEY = "pendingSignupDraft";

export function writePendingSignupDraft(draft: SignupDraft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // She writes it again after signing in: the page still works.
  }
}

/** Her draft for this token, or null: none, another business's, or not of a draft's shape. */
export function readPendingSignupDraft(token: string): SignupDraft | null {
  try {
    const draft = draftOf(JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? "null"));
    return draft !== null && draft.token === token ? draft : null;
  } catch {
    return null;
  }
}

/**
 * «Pedir servicio» on one item's page: the profile's request with
 * «¿Qué necesitas?» already naming the item. Her name, her WhatsApp and her
 * day, kept for this business, stay; the day is left to the form when none
 * was chosen (a day not on offer falls to the first one).
 */
export function prefillPendingSignupWish(token: string, description: string): void {
  const kept = readPendingSignupDraft(token);
  writePendingSignupDraft({
    token,
    name: kept?.name ?? "",
    phone: kept?.phone ?? "",
    wish: { preferredOn: kept?.wish.preferredOn ?? "", preferredTime: kept?.wish.preferredTime ?? null, description },
  });
}

export function clearPendingSignupDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // Nothing to undo if storage cannot be touched.
  }
}

/** Only fields of the right type come back: storage is anybody's to edit. */
function draftOf(value: unknown): SignupDraft | null {
  if (typeof value !== "object" || value === null) return null;
  const { token, name, phone, wish } = value as Record<string, unknown>;
  if (typeof token !== "string" || typeof name !== "string" || typeof phone !== "string") return null;
  if (typeof wish !== "object" || wish === null) return null;
  const { description, preferredOn, preferredTime } = wish as Record<string, unknown>;
  if (typeof description !== "string" || typeof preferredOn !== "string") return null;
  if (preferredTime !== null && typeof preferredTime !== "string") return null;
  return { token, name, phone, wish: { description, preferredOn, preferredTime } };
}
